import { useEffect, useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { useActiveFleet } from '@/data/fleets';
import {
  useRequestRecheck,
  useUpdateVehicle,
  useVehicleVerification,
  useVehicles,
} from '@/data/vehicles';
import { useDriversList } from '@/data/drivers';
import { Spinner } from '@/components/ui/spinner';
import { EmptyState } from '@/components/empty-state';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { VerificationBadge } from '@/components/verification-badge';
import { useToast } from '@/components/ui/toast';
import { formatDate, formatRelative } from '@/lib/format';
import { vehicleTypeLabel } from '@/pages/vehicles';
import type { Vehicle } from '@breaker/shared-types';

const CHECK_LABELS: Record<string, { name: string; blurb: string }> = {
  dvla_ves: {
    name: 'DVLA — Vehicle Enquiry Service',
    blurb: 'Tax status and due date, held by DVLA.',
  },
  dvsa_mot_history: {
    name: 'DVSA — MOT History',
    blurb: 'MOT test result, expiry and odometer, held by DVSA.',
  },
};

export function VehicleDetailPage() {
  const { vehicleId } = useParams({ from: '/authed/vehicles/$vehicleId' });
  const { fleet } = useActiveFleet();
  const vehiclesQuery = useVehicles(fleet?.id ?? null);
  const verificationQuery = useVehicleVerification(fleet?.id ?? null, vehicleId);
  const recheck = useRequestRecheck(fleet?.id ?? null, vehicleId);

  const vehicle = (vehiclesQuery.data ?? []).find((v) => v.id === vehicleId);

  if (vehiclesQuery.isLoading || verificationQuery.isLoading) {
    return (
      <div className="flex justify-center py-24">
        <Spinner size="lg" />
      </div>
    );
  }
  if (!vehicle) {
    return <EmptyState title="Vehicle not found" description="It may have been removed from the fleet." />;
  }

  const detail = verificationQuery.data;

  return (
    <div className="px-8 py-10 max-w-[900px]">
      <header className="mb-8">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold tracking-tight font-mono text-breaker-ink">
            {vehicle.registration}
          </h1>
          <VerificationBadge verification={detail?.verification} />
        </div>
        <p className="text-sm text-breaker-mute mt-1">
          {vehicleTypeLabel(vehicle.type)}
          {[vehicle.make, vehicle.model].filter(Boolean).length > 0 &&
            ` · ${[vehicle.make, vehicle.model].filter(Boolean).join(' ')}`}
        </p>
      </header>

      <OwnershipCard fleetId={fleet?.id ?? null} vehicle={vehicle} />

      <section className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-breaker-mute">
            Government verification
          </h2>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            action={() => recheck.mutateAsync(undefined).catch(() => undefined)}
            className="h-auto rounded-md border border-breaker-line px-3 py-1.5 text-xs font-medium text-breaker-ink hover:border-breaker-identity hover:text-breaker-identity"
          >
            {recheck.isPending ? 'Queuing…' : recheck.isSuccess ? 'Queued ✓' : 'Re-check now'}
          </Button>
        </div>

        {!detail || detail.checks.length === 0 ? (
          <div className="surface rounded-lg p-5 text-sm text-breaker-mute">
            No checks have run yet. The nightly sweep covers every active
            vehicle at 04:45 — or press &ldquo;Re-check now&rdquo;.
          </div>
        ) : (
          <div className="space-y-3">
            {detail.checks.map((c) => {
              const meta = CHECK_LABELS[c.checkType] ?? { name: c.checkType, blurb: '' };
              const summary = (c.summary ?? {}) as Record<string, string | undefined>;
              return (
                <div key={c.checkType} className="surface rounded-lg p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-breaker-ink">{meta.name}</p>
                      <p className="text-xs text-breaker-mute mt-0.5">{meta.blurb}</p>
                    </div>
                    <CheckStatusBadge status={c.status} />
                  </div>

                  <dl className="grid grid-cols-3 gap-x-6 gap-y-2 mt-4 text-sm">
                    {c.checkType === 'dvla_ves' && (
                      <>
                        <Fact label="Tax status" value={summary.taxStatus} />
                        <Fact label="Tax due" value={summary.taxDueDate ? formatDate(summary.taxDueDate) : undefined} />
                        <Fact label="Fuel" value={summary.fuelType} />
                      </>
                    )}
                    {c.checkType === 'dvsa_mot_history' && (
                      <>
                        <Fact label="MOT status" value={summary.motStatus} />
                        <Fact label="MOT expires" value={summary.motExpiryDate ? formatDate(summary.motExpiryDate) : undefined} />
                        <Fact label="Last test" value={summary.lastTestDate ? formatDate(summary.lastTestDate) : undefined} />
                      </>
                    )}
                    <Fact
                      label="Checked"
                      value={c.checkedAt ? formatRelative(c.checkedAt) : 'never'}
                    />
                  </dl>

                  {c.mismatches.length > 0 && (
                    <ul className="mt-4 space-y-1">
                      {c.mismatches.map((m) => (
                        <li key={m} className="text-sm text-breaker-red-deep">
                          ⚠ {m}
                        </li>
                      ))}
                    </ul>
                  )}
                  {c.status === 'unavailable' && (
                    <p className="mt-3 text-xs text-breaker-mute">
                      This provider isn&rsquo;t configured yet — Breaker never
                      shows a green tick it can&rsquo;t back up.
                    </p>
                  )}
                  {c.status === 'error' && c.error && (
                    <p className="mt-3 text-xs text-breaker-mute">
                      Provider trouble on the last attempt; will retry. ({c.error})
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <p className="text-xs text-breaker-mute">
        Checks run against official DVLA and DVSA records and refresh
        automatically every night. Data is a point-in-time snapshot from the
        government service, not legal proof of roadworthiness.
      </p>
    </div>
  );
}

function OwnershipCard({ fleetId, vehicle }: { fleetId: string | null; vehicle: Vehicle }) {
  const driversQuery = useDriversList(fleetId);
  const update = useUpdateVehicle(fleetId);
  const toast = useToast();
  const [ownerId, setOwnerId] = useState(vehicle.ownerDriverUserId ?? '');
  useEffect(() => {
    setOwnerId(vehicle.ownerDriverUserId ?? '');
  }, [vehicle.id, vehicle.ownerDriverUserId]);

  const dirty = ownerId !== (vehicle.ownerDriverUserId ?? '');

  return (
    <section className="mb-8">
      <h2 className="text-sm font-semibold uppercase tracking-wider text-breaker-mute mb-3">
        Ownership
      </h2>
      <div className="surface rounded-lg p-5 flex flex-wrap items-end gap-4">
        <label className="block min-w-[220px]">
          <span className="text-2xs font-semibold uppercase tracking-wider text-breaker-mute">
            Owner
          </span>
          <select
            value={ownerId}
            onChange={(e) => setOwnerId(e.target.value)}
            className="mt-1 w-full rounded-md border border-breaker-line bg-breaker-ground px-3 py-2 text-sm focus:border-breaker-identity focus:outline-none"
          >
            <option value="">Fleet pool</option>
            {(driversQuery.data ?? []).map((d) => (
              <option key={d.userId} value={d.userId}>
                {d.displayName}
              </option>
            ))}
          </select>
        </label>
        <Button
          type="button"
          disabled={!dirty}
          action={() =>
            update
              .mutateAsync({ vehicleId: vehicle.id, body: { ownerDriverUserId: ownerId || null } })
              .then(() => toast.success('Ownership updated'))
              .catch((e) =>
                toast.error(e instanceof Error ? e.message : 'Update failed'),
              )
          }
        >
          {update.isPending ? 'Saving…' : 'Save'}
        </Button>
        <p className="text-xs text-breaker-mute basis-full">
          Pool vehicles are assignable to any driver; a driver-owned vehicle only rides
          with its owner. Changing owner clears it as another driver&rsquo;s default
          automatically if it was one.
        </p>
      </div>
    </section>
  );
}

function CheckStatusBadge({ status }: { status: string }) {
  if (status === 'passed') return <Badge tone="success">Passed</Badge>;
  if (status === 'failed') return <Badge tone="danger">Failed</Badge>;
  if (status === 'error') return <Badge tone="active">Retrying</Badge>;
  return <Badge tone="neutral">{status === 'unavailable' ? 'Not configured' : status}</Badge>;
}

function Fact({ label, value }: { label: string; value: string | undefined }) {
  return (
    <div>
      <dt className="text-2xs font-semibold uppercase tracking-wider text-breaker-mute">{label}</dt>
      <dd className="text-breaker-ink mt-0.5">{value ?? '—'}</dd>
    </div>
  );
}
